/**
 * Store: Garage
 * -----------------------------------------------------------------------------
 * Handles the local storage of vehicles in the user's garage.
 * - The My Garage component is responsible for interfacing with the user's
 *   stored garage that has been saved to their customer metafield.
 *
 */
import { formatVehicle, getVehicleCompatibility } from '~/helpers/garage'

/**
 * @typedef {Object} Vehicle - Vehicle object.
 * @property {Object} details - Vehicle details object generated from vehicle
 * details theme setting.
 * @property {String} form - Form used to add vehicle to garage, used for
 * tracking events.
 * @property {VehicleId} id - Unique ID for vehicle generated locally.
 * @property {String} method - Method used to add vehicle to garage, `vrm`,
 * `dropdowns`, or `vin`.
 * @property {String} query - String used as query in API request.
 * @property {Number} motUpdated - Number of times vehicle has had its MOT
 * updated, used in conjunction with MOT refresh button.
 * @property {UserDetails} user - User's unique vehicle details.
 */

/**
 * @typedef {Object} FormattedVehicle - Vehicle object.
 * @property {Object} details - Vehicle details object.
 * @property {String} form - Form used to add vehicle to garage, used for
 * tracking events.
 * @property {Array<filter>} filters - Storefront API compatible filter object.
 * @property {VehicleId} id - Unique ID for vehicle.
 * @property {String} method - Method used to add vehicle to garage, `vrm`,
 * `dropdowns`, or `vin`.
 * @property {String} resultsUrl - Vehicles results collection URL with filters.
 * @property {VehicleStrings} strings - Vehicle strings for display.
 * @property {UserDetails} user - User's unique vehicle details.
 */

/**
 * @typedef {Object} filter - Storefront API filter.
 * @property {ProductMetafield} productMetafield - Object for product metafield.
 */

/**
 * @typedef {Object} ProductMetafield - Product metafield filter.
 * @property {String} key - Metafield key.
 * @property {String} namespace - Metafield namespace.
 * @property {String} value - Metafield value.
 */

/**
 * @typedef {String} VehicleId - Unique vehicle ID, built from vehicle details.
 */

/**
 * @typedef {Object} VehicleStrings - Vehicle strings for display.
 * @property {String} title - Title string.
 * @property {String} text - Text string.
 */

/**
 * @typedef {Object} UserDetails - User's unique vehicle details.
 * @property {Date} mot - MOT due date.
 * @property {String} vrm - Vehicle registration mark (formatted).
 * @property {String} vin - Vehicle identification number.
 */
export default {
  namespaced: true,

  /**
   * Do not directly access vehicles in state, use getters instead to add
   * additional data.
   */
  state: () => ({
    active: false,
    garage: [],
    onlineProducts: [],
    showCompatibility: true,
  }),

  getters: {

    /**
     * Get formatted active vehicle object.
     * @returns {FormattedVehicle}
     */
    getActiveVehicle(state, getters) {
      const activeVehicle = getters.getVehicleById(state.active)
      return getters.getFormattedVehicle(activeVehicle)
    },

    /**
     * Get compatibility of product taxonomy and active vehicle.
     * @param {Object} productTaxonomy - Product taxonomy data.
     * @returns {String} `compatible`, `partial`, `incompatible`, or `none`.
     */
    getCompatibility: (state, getters) => (productTaxonomy) => {
      if (!state.active) {
        return 'none'
      }

      if (!productTaxonomy || !Object.keys(productTaxonomy)?.length) {
        return 'incompatible'
      }

      const activeVehicleDetails = getters.getActiveVehicle?.details
      const productVehicleDetails = cnvs.vehicleFinder.productVehicleDetails
      const subModelHandle = cnvs.vehicleFinder.handles.subModel
      const vehicleDetails = Object.values(cnvs.vehicleFinder.vehicleDetails)

      /**
       * Filter product taxonomy to match items in product vehicle details theme
       * setting.
       * - Also product detail must have a value.
       */
      const productDetails = Object.entries(productTaxonomy)
        .map(([key, value]) => {
          const vehicleDetail = vehicleDetails.find((detail) => detail.key === key)

          if (
            !vehicleDetail ||
            !productVehicleDetails?.includes(vehicleDetail.handle) ||
            !value
          ) {
            return false
          }

          return {
            handle: vehicleDetail.handle,
            key,
            value,
          }
        })
        .filter(Boolean)

      /**
       * Determine if active vehicle details match all product details.
       */
      const matchTests = productDetails
        .map(({ handle, key, value }) => {
          const { matches } = getVehicleCompatibility(key, value, activeVehicleDetails)

          return {
            handle,
            key,
            matches,
          }
        })
        .filter(Boolean)

      /**
       * If any tests return as incompatible then the whole product is
       * incompatible with the active vehicle.
       */
      if (matchTests.some((detail) => detail.matches === 'incompatible')) {
        return 'incompatible'
      }

      /**
       * If compatible test results matches number of product details then the
       * whole product is compatible.
       * - Otherwise it's a partial match.
       * - Add sub-model to compatible count if it has 'none' as its matches
       *   value, sub-model isn't required for a COMPATIBLE product.
       */
      const compatible = matchTests.filter((detail) => {
        if (detail.handle === subModelHandle && detail.matches === 'none') {
          return true
        }

        return detail.matches === 'compatible'
      })

      return compatible.length === productDetails.length
        ? 'compatible'
        : 'maybe'
    },

    /**
     * Get formatted vehicle object.
     * @param {Vehicle} vehicle - Vehicle to format.
     * @returns {FormattedVehicle}
     */
    getFormattedVehicle: () => (vehicle) => {
      return formatVehicle(vehicle)
    },

    /**
     * Get garage count.
     * @returns {Number}
     */
    getGarageCount(state) {
      return state.garage.length
    },

    /**
     * Get non-active vehicles from garage.
     * @returns {Array<FormattedVehicle>}
     */
    getNonActiveVehicles(state, getters) {
      const vehicles = state.active
        ? state.garage.filter((vehicle) => {
          return vehicle.id !== state.active
        })
        : state.garage

      return vehicles.map((vehicle) => {
        return getters.getFormattedVehicle(vehicle)
      })
    },

    /**
     * Get vehicle in garage by ID.
     * @param {VehicleId} id - Vehicle ID to find.
     * @returns {Boolean|FormattedVehicle}
     */
    getVehicleById: (state, getters) => (id) => {
      const foundVehicle = state.garage.find((vehicle) => vehicle.id === id)

      if (!foundVehicle) {
        return false
      }

      return getters.getFormattedVehicle(foundVehicle)
    },

    /**
     * Get vehicle index in garage by ID.
     * @param {VehicleId} id - Vehicle ID to find.
     * @returns {Number}
     */
    getVehicleIndexById: (state) => (id) => {
      return state.garage.findIndex((vehicle) => vehicle.id === id)
    },
  },

  mutations: {

    /**
     * Set active vehicle.
     * @param {Number} index - Index of vehicle in garage array.
     * @param {VehicleId} vehicleId - Vehicle to set as active.
     */
    SET_ACTIVE_VEHICLE(state, { index, vehicleId }) {
      state.active = vehicleId

      if (!vehicleId || index < 0) {
        return
      }

      /**
       * Move new active vehicle to top of garage array.
       */
      state.garage.unshift(state.garage.splice(index, 1)[0])
    },

    /**
     * Set add vehicle.
     * @param {Boolean} [setActive] - Set vehicle as active even if already an
     * active vehicle already exists.
     * @param {Vehicle} vehicle - Vehicle to add to garage.
     */
    SET_ADD_VEHICLE(state, { setActive, vehicle }) {
      state.garage.unshift(vehicle)

      if (state.active && !setActive) {
        return
      }

      state.active = vehicle.id
    },

    /**
     * Set online products.
     * @param {Array} onlineProducts - Online products
     */
    SET_ONLINE_PRODUCTS(state, onlineProducts) {
      state.onlineProducts = onlineProducts
    },

    /**
     * Set remove vehicle.
     * @param {Number} index - Vehicle index in garage.
     */
    SET_REMOVE_VEHICLE(state, index) {
      state.garage.splice(index, 1)
    },

    /**
     * Set show compatibility.
     * @param {Boolean} showCompatibility - Show compatibility?
     */
    SET_SHOW_COMPATIBILITY(state, showCompatibility) {
      state.showCompatibility = showCompatibility
    },

    /**
     * Set updated garage.
     * @param {Array<Vehicle>} garage - Complete array to update with.
     */
    SET_UPDATE_GARAGE(state, garage) {
      state.garage = garage
    },

    /**
     * Set updated vehicle.
     * @param {Number} index - Vehicle index in garage.
     * @param {Vehicle} vehicle - Updated vehicle object.
     */
    SET_UPDATE_VEHICLE(state, { index, vehicle }) {
      state.garage[index] = vehicle
    },
  },

  actions: {

    /**
     * Add vehicle to garage.
     * - If no vehicle is active then it's set as active too.
     * @param {Boolean} [setActive] - Set vehicle as active even if there is
     * already an active vehicle.
     * @param {Vehicle} vehicle - Vehicle to add to garage, if it already exists
     * then it's ignored.
     */
    add({ commit, getters }, { setActive, vehicle } = {}) {
      if (!vehicle.id || getters.getVehicleById(vehicle.id)) {
        return
      }

      commit('SET_ADD_VEHICLE', { setActive, vehicle })
    },

    /**
     * Update online products.
     * @param {Array} onlineProducts - Online products
     */
    onlineProducts({ commit }, onlineProducts) {
      commit('SET_ONLINE_PRODUCTS', onlineProducts)
    },

    /**
     * Open finder flow action.
     * - Shorthand for opening vehicle finder or garage modals.
     * @param {Object|String} [data] - Event type string, `garage`, `finder`,
     * `compatibility` or `both` which picks based on garage count. Or an object
     * with the product compatibility data to emit.
     * @param {Object} [data.product] - Compatibility product.
     * @param {String} [data.type] - Type of data object, 'compatibility'.
     */
    openFinderFlow({ dispatch, getters }, data) {
      const type = typeof data === 'string' ? data : data.type

      let object = {
        component: 'vehicle-finder',
        namespace: 'vehicleFinderModal',
      }

      if (
        type === 'garage' ||
        (type === 'both' && getters.getGarageCount)
      ) {
        object = {
          component: 'garage-modal',
          namespace: 'garageModal',
        }
      }

      if (type === 'compatibility') {
        object = {
          component: 'compatibility-drawer',
          namespace: 'compatibilityDrawer',
        }

        if (typeof data === 'object') {
          dispatch('setCompatibilityProduct', data.product, { root: true })
        }
      }

      dispatch('overlays/open', {
        component: object.component,
        ignoreDismissed: true,
        namespace: object.namespace,
      }, { root: true })
    },

    /**
     * Remove vehicle from garage.
     * - Ignored if vehicle doesn't exist in garage.
     * @param {VehicleId} vehicleId - Vehicle ID to remove.
     */
    remove({ commit, getters }, vehicleId) {
      const index = getters.getVehicleIndexById(vehicleId)

      if (index < 0) {
        return
      }

      commit('SET_REMOVE_VEHICLE', index)
    },

    /**
     * Set active vehicle.
     * - Also moves vehicle to top of garage list.
     * @param {VehicleId|undefined} vehicleId - Vehicle to set as active, pass
     * no vehicle to remove active vehicle, removing an active vehicle doesn't
     * remove it from the garage.
     */
    setActive({ commit, getters }, vehicleId) {
      const index = vehicleId ? getters.getVehicleIndexById(vehicleId) : -1
      commit('SET_ACTIVE_VEHICLE', { index, vehicleId })
    },

    /**
     * Update show compatibility.
     * @param {Boolean} showCompatibility - Show compatibility?
     */
    showCompatibility({ commit }, showCompatibility) {
      commit('SET_SHOW_COMPATIBILITY', showCompatibility)
    },

    /**
     * Update garage array.
     * @param {Array<Vehicle>} garage - Complete array to update with.
     */
    updateGarage({ commit }, garage) {
      commit('SET_UPDATE_GARAGE', garage)
    },

    /**
     * Update vehicle in garage.
     * - Pass partial vehicle object to update properties.
     * - Vehicle ID can't be updated.
     * - Does nothing if vehicle ID can't be found.
     * @param {VehicleId|Object} data - Either vehicle ID or update options.
     * @param {VehicleId} data.vehicleId - Vehicle ID to match.
     * @param {Object} [data.updates] - Partial vehicle object.
     */
    updateVehicle({ commit, getters }, data) {
      let updates = {}
      let vehicleId = data

      if (typeof data === 'object') {
        updates = data.updates
        vehicleId = data.vehicleId
      }

      const match = getters.getVehicleById(vehicleId)
      const index = getters.getVehicleIndexById(vehicleId)

      if (!match) {
        return
      }

      /**
       * Vehicle ID and updated are ignored in vehicle updates.
       */
      const vehicle = match

      Object.entries(updates).forEach(([key, value]) => {
        vehicle[key] = {
          ...vehicle[key],
          ...value,
        }
      })

      vehicle.id = match.id
      vehicle.motUpdated = match.motUpdated + 1

      commit('SET_UPDATE_VEHICLE', { index, vehicle })
    },
  },
}
